import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import { useState, useEffect, useContext } from "react";
import {
  User,
  Bell,
  Building2,
  LogOut,
  Pencil,
  Mail,
  Monitor,
  ShoppingBag,
  Package,
  Gavel,
  CheckCircle2,
  Globe,
  Hash,
  Phone,
  AlertTriangle,
} from "lucide-react";
import EditProfileModal from "../../../components/admin/admineditProfile/AdminEditProfile";
import "../../../assets/css/admin/adminSettings/AdminSettings.css";

/* ── Default Data ── */
const getInitials = (name) =>
  name
    ?.split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();

/* Formats a raw ISO timestamp (e.g. "2026-07-17T07:08:35.000000Z")
   into a readable form like "17 Jul 2026, 07:08 AM" */
const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (isNaN(date.getTime())) return value; // fallback if not a valid date

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/* Default shape so JSX never breaks before the API responds.
   Matches the backend response exactly — flat, no nested objects. */
const defaultAdmin = {
  name: "",
  email: "",
  phone: "",
  location: "",
  last_login: "",
  created_at: "",

  business_name: "",
  business_phone: "",
  business_email: "",
  business_address: "",

  email_notifications: false,
  system_notifications: false,
  order_updates: false,
  inventory_alerts: false,
  bid_activity: false,
};

/* ─────────────────────────────────────────── */
const AdminSettings = () => {
  const { baseUrl, authToken, setLoggedInAdmin } = useContext(AppContext); // 👈 setLoggedInAdmin add kiya
  const [modalOpen, setModalOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [admin, setAdmin] = useState(defaultAdmin);

  /* handlers */
  const handleToggle = (key) =>
    setAdmin((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

  const handleSave = (data) => {
    // Modal sends: name, email, phone, businessName, businessPhone, businessEmail, address
    // Backend expects flat snake_case fields — map before sending.
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      location: data.location,

      business_name: data.businessName,
      business_phone: data.businessPhone,
      business_email: data.businessEmail,
      business_address: data.address,

      email_notifications: admin.email_notifications,
      system_notifications: admin.system_notifications,
      order_updates: admin.order_updates,
      inventory_alerts: admin.inventory_alerts,
      bid_activity: admin.bid_activity,
    };

    setSaving(true);

    axios
      .post(`${baseUrl}/admin/auth/updateAdminData`, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        fetchProfile();
        setModalOpen(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const fetchProfile = () => {
    axios
      .get(`${baseUrl}/admin/auth/viewMyProfile`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          setAdmin(response.data.admin);

          // 👇 Context + localStorage bhi update kar rahe hain
          // taake sidebar (aur app ke doosre parts) turant naya data dekhein
          setLoggedInAdmin(response.data.admin);
          localStorage.setItem(
            "loggedInAdmin",
            JSON.stringify(response.data.admin)
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (authToken) {
      fetchProfile();
    }
  }, [authToken]);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are about to logout from Admin Panel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
      background: "#1e1e1e",
      color: "#fff",
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .post(
          `${baseUrl}/admin/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        )
        .then((response) => {
          if (response.data.success) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("loggedInAdmin");

            Swal.fire({
              title: "Logged out",
              text: "You have been logged out successfully.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
              background: "#1e1e1e",
              color: "#fff",
            }).then(() => {
              window.location.href = "/admin/login";
            });
          }
        })
        .catch((error) => {
          console.log(error);
          Swal.fire({
            title: "Error",
            text: "Something went wrong while logging out.",
            icon: "error",
            background: "#1e1e1e",
            color: "#fff",
          });
        });
    });
  };

  /* notification config */
  const notifItems = [
    {
      key: "email_notifications",
      label: "Email Notifications",
      sub: "Orders, alerts & weekly summaries via email",
      Icon: Mail,
    },
    {
      key: "system_notifications",
      label: "System Notifications",
      sub: "In-app banners, alerts and pop-ups",
      Icon: Monitor,
    },
    {
      key: "order_updates",
      label: "Order Updates",
      sub: "New, cancelled or modified order alerts",
      Icon: ShoppingBag,
    },
    {
      key: "inventory_alerts",
      label: "Inventory Alerts",
      sub: "Low stock warnings and restock reminders",
      Icon: Package,
    },
    {
      key: "bid_activity",
      label: "Bid Activity",
      sub: "New bids, bid closings and winner alerts",
      Icon: Gavel,
    },
  ];

  /* business info rows */
  const bizItems = [
    { label: "Business Name", value: admin.business_name },
    { label: "Phone", value: admin.business_phone },
    { label: "Email", value: admin.business_email },
  ];

  /* ─── Render ─── */
  return (
    <div className="asp-wrapper">
      {/* Save Toast */}
      {saved && (
        <div className="asp-save-toast">
          <CheckCircle2 size={15} />
          Changes saved successfully!
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="asp-page-header">
        <h2 className="asp-page-title">Settings</h2>
      </div>

      {/* ══════════════════════════════════
            1. ADMIN PROFILE CARD
        ══════════════════════════════════ */}
      <div className="asp-card">
        <div className="asp-card-header">
          <div className="asp-card-title">
            <div className="asp-card-icon">
              <User size={16} />
            </div>
            Admin Profile
          </div>
          <button className="asp-edit-btn" onClick={() => setModalOpen(true)}>
            <Pencil size={13} />
            Edit Profile
          </button>
        </div>

        {/* Avatar + basic info */}
        <div className="asp-profile-row">
          <div className="asp-avatar">
            {getInitials(admin.name)}
            <span className="asp-avatar-online" />
          </div>
          <div className="asp-profile-meta">
            <div className="asp-profile-name">{admin.name}</div>
            <span className="asp-profile-badge">Super Admin</span>
            <div className="asp-profile-email">{admin.email}</div>
          </div>
        </div>

        {/* Detail grid */}
        <div className="asp-profile-grid">
          <div className="asp-info-item">
            <label>Phone</label>
            <span>{admin.phone}</span>
          </div>
          <div className="asp-info-item">
            <label>Location</label>
            <span>{admin.location || "—"}</span>
          </div>
          <div className="asp-info-item">
            <label>Member Since</label>
            <span>{formatDate(admin.created_at)}</span>
          </div>
          <div className="asp-info-item">
            <label>Last Login</label>
            <span>{formatDate(admin.last_login)}</span>
          </div>
        </div>
      </div>
      {/* ══════════════════════════════════
            3. BUSINESS INFORMATION CARD
        ══════════════════════════════════ */}
      <div className="asp-card">
        <div className="asp-card-header">
          <div className="asp-card-title">
            <div className="asp-card-icon">
              <Building2 size={16} />
            </div>
            Business Information
          </div>
          <button className="asp-edit-btn" onClick={() => setModalOpen(true)}>
            <Pencil size={13} />
            Edit
          </button>
        </div>

        <div className="asp-biz-grid">
          {bizItems.map(({ label, value }) => (
            <div className="asp-info-item" key={label}>
              <label>{label}</label>
              <span>{value}</span>
            </div>
          ))}
          <div className="asp-info-item asp-full">
            <label>Address</label>
            <span>{admin.business_address}</span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════
            4. LOGOUT CARD
        ══════════════════════════════════ */}
      <div className="asp-logout-card">
        <div className="asp-logout-info">
          <div className="asp-logout-icon-box">
            <AlertTriangle size={18} />
          </div>
          <div className="asp-logout-text">
            <strong>Logout from Admin Panel</strong>
            <span>You will be redirected to the login page.</span>
          </div>
        </div>
        <button className="asp-logout-btn" onClick={handleLogout}>
          <LogOut size={15} />
          Logout
        </button>
      </div>

      {/* ── Edit Modal ── */}
      <EditProfileModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        admin={admin}
        onSave={handleSave}
        isSaving={saving}
      />
    </div>
  );
};

export default AdminSettings;